<template>
  <div
    class="wago-energy-stats mb-3"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Top: Active + Reactive totals side by side -->
    <div class="row g-0">
      <div class="col-6 stats-total-tile stats-total-tile--active border-end-subtle">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <p class="stats-total-label">
              {{ $t('components.assetDetails.wagoStats.energyActive') }}
            </p>
            <p class="stats-total-age">
              {{ activeRelativeTime }}
            </p>
          </div>
        </div>
        <div class="stats-total-value mt-2">
          <span v-if="stats.active.current !== null">{{ formatNumber(stats.active.current) }}</span>
          <span v-else>—</span>
          <span class="stats-total-unit">{{ stats.active.unit }}</span>
        </div>
      </div>

      <div class="col-6 stats-total-tile stats-total-tile--reactive">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <p class="stats-total-label">
              {{ $t('components.assetDetails.wagoStats.energyReactive') }}
            </p>
            <p class="stats-total-age">
              {{ reactiveRelativeTime }}
            </p>
          </div>
        </div>
        <div class="stats-total-value mt-2">
          <span v-if="stats.reactive.current !== null">{{ formatNumber(stats.reactive.current) }}</span>
          <span v-else>—</span>
          <span class="stats-total-unit">{{ stats.reactive.unit }}</span>
        </div>
      </div>
    </div>

    <!-- Period cards (active energy only): 2 per row -->
    <div class="row g-0 stats-periods-border-top">
      <div
        v-for="(period, idx) in periodCards"
        :key="period.key"
        class="col-6 stats-period-cell"
        :class="{ 'border-end-subtle': idx % 2 === 0 }"
      >
        <div class="d-flex justify-content-between align-items-center h-100">
          <div>
            <p class="measurement-name">
              {{ period.label }}
            </p>
            <p class="measurement-date">
              {{ activeRelativeTime || '—' }}
            </p>
          </div>
          <div class="unit-value-box">
            <template v-if="period.value !== null">
              <div class="period-value">
                +<span>{{ formatNumber(period.value) }}</span>
              </div>
              <div class="period-unit">
                {{ stats.active.unit }}
              </div>
            </template>
            <template v-else>
              <div class="period-value no-data">
                —
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/pl'

dayjs.extend(relativeTime)

interface EnergyStat {
  current: number | null
  lastUpdateTs: number | null
  unit: string
}

interface ActiveEnergyStat extends EnergyStat {
  today: number | null
  yesterday: number | null
  thisWeek: number | null
  thisMonth: number | null
  lastMonth: number | null
  thisYear: number | null
}

export interface EnergyStatsPayload {
  active: ActiveEnergyStat
  reactive: EnergyStat
}

type PeriodKey = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear'

export default defineComponent({
  name: 'WagoEnergyStats',
  props: {
    stats: {
      type: Object as PropType<EnergyStatsPayload>,
      required: true
    },
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    activeRelativeTime(): string {
      const ts = this.stats.active?.lastUpdateTs
      if (!ts) return ''
      return dayjs.unix(ts).locale(this.$i18n?.locale ?? 'pl').fromNow()
    },
    reactiveRelativeTime(): string {
      const ts = this.stats.reactive?.lastUpdateTs
      if (!ts) return ''
      return dayjs.unix(ts).locale(this.$i18n?.locale ?? 'pl').fromNow()
    },
    periodCards(): { key: PeriodKey; label: string; value: number | null }[] {
      const periods: { key: PeriodKey; labelKey: string }[] = [
        { key: 'today',     labelKey: 'components.assetDetails.wagoStats.today' },
        { key: 'yesterday', labelKey: 'components.assetDetails.wagoStats.yesterday' },
        { key: 'thisWeek',  labelKey: 'components.assetDetails.wagoStats.thisWeek' },
        { key: 'thisMonth', labelKey: 'components.assetDetails.wagoStats.thisMonth' },
        { key: 'lastMonth', labelKey: 'components.assetDetails.wagoStats.lastMonth' },
        { key: 'thisYear',  labelKey: 'components.assetDetails.wagoStats.thisYear' },
      ]
      return periods.map(p => ({
        key: p.key,
        label: this.$t(p.labelKey) as string,
        value: this.stats.active?.[p.key] ?? null
      }))
    }
  },
  methods: {
    formatNumber(value: number): string {
      return value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
})
</script>

<style scoped>
.wago-energy-stats {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #ffffff;
}

.dark-mode.wago-energy-stats {
  background: #1e1e2e;
  border-color: rgba(255, 255, 255, 0.08);
}

/* ── Total tiles ── */
.stats-total-tile {
  padding: 18px 20px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}

.dark-mode .stats-total-tile {
  border-bottom-color: rgba(255, 255, 255, 0.07);
}

.stats-total-tile--active {
  background: rgba(254, 226, 226, 0.45);
}

.dark-mode .stats-total-tile--active {
  background: rgba(127, 29, 29, 0.2);
}

.stats-total-tile--reactive {
  background: rgba(254, 252, 232, 0.55);
}

.dark-mode .stats-total-tile--reactive {
  background: rgba(78, 63, 0, 0.2);
}

.border-end-subtle {
  border-right: 1px solid rgba(15, 23, 42, 0.07);
}

.dark-mode .border-end-subtle {
  border-right-color: rgba(255, 255, 255, 0.07);
}

.stats-total-label {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
  color: #374151;
}

.dark-mode .stats-total-label {
  color: #cbd5e1;
}

.stats-total-age {
  margin: 2px 0 0;
  font-size: 0.96rem;
  color: #94a3b8;
}

.stats-total-value {
  font-size: 1.96rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.dark-mode .stats-total-value {
  color: #f1f5f9;
}

.stats-total-unit {
  font-size: 1.11rem;
  font-weight: 600;
  color: #6b7280;
}

.dark-mode .stats-total-unit {
  color: #94a3b8;
}

/* ── Period cells ── */
.stats-periods-border-top {
  /* row inherits from Bootstrap */
}

.stats-period-cell {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
  min-height: 64px;
}

.stats-period-cell:nth-last-child(-n+2) {
  border-bottom: none;
}

.dark-mode .stats-period-cell {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

/* ── Card content ── */
.measurement-name {
  margin: 0;
  font-size: 1.04rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-mode .measurement-name {
  color: #cbd5e1;
}

.measurement-date {
  margin: 2px 0 0;
  font-size: 0.96rem;
  color: #94a3b8;
}

.unit-value-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  margin-left: 8px;
}

.period-value {
  font-size: 1.26rem;
  font-weight: 700;
  color: #b91c1c;
  white-space: nowrap;
}

.dark-mode .period-value {
  color: #fca5a5;
}

.period-value.no-data {
  color: #94a3b8;
}

.period-unit {
  font-size: 0.98rem;
  font-weight: 500;
  color: #6b7280;
}

.dark-mode .period-unit {
  color: #94a3b8;
}
</style>
